import { isNil } from 'lodash';

import {
  BookingPatient,
  IFamilyBookingPatientService,
  SelectedPatient,
} from 'interfaces/familyBookingTypes';
import { BookableSelectedPatient } from 'services/APIs';

import {
  MYSELF_AND_OTHERS_OPTION,
  ModeOption,
  OTHERS_OPTION,
} from '../../utils/constants';

export const MAX_PATIENTS_PER_BOOKING = 4;

export const MAX_PATIENTS_MESSAGE = `You’ve reached the maximum ${MAX_PATIENTS_PER_BOOKING} patients per family booking`;

//--The search reports bookability as a bare flag, with no reason to pass along--
export const UNBOOKABLE_PATIENT_MESSAGE = 'This patient can not be booked';

//--Names the mode option so renaming the dropdown entry can't strand this instruction--
export const UNBOOKABLE_POINT_OF_CONTACT_MESSAGE = `The Point of Contact patient can not be booked, please change to ${OTHERS_OPTION.label} to continue.`;

export const isAllowedToAddPatient = ({
  mode,
  patients,
}: {
  mode: ModeOption;
  patients: BookingPatient[];
}) => {
  const scheduledPatientCount = patients.filter(
    (item) => !isNil(item.serviceId)
  ).length;

  const isMyselfAndOthersMode = mode.id === MYSELF_AND_OTHERS_OPTION.id;

  const isFamilyMode = [MYSELF_AND_OTHERS_OPTION.id, OTHERS_OPTION.id].includes(
    mode.id
  );

  const pointOfContactCount = isMyselfAndOthersMode ? 1 : 0;

  const totalPatientCount = scheduledPatientCount + pointOfContactCount;

  const hasCapacity = totalPatientCount < MAX_PATIENTS_PER_BOOKING;

  return isFamilyMode && hasCapacity;
};

export const hasHomeAddressOnFile = (
  patient: Pick<BookingPatient, 'homeAddress'>
) => !!patient.homeAddress?.trim();

//--Manually added family members carry no bookability flag, so only an explicit false blocks them--
export const isPatientBookable = (patient: Pick<BookingPatient, 'isBookable'>) =>
  patient.isBookable !== false;

//--A slot blocked only by a same-time appointment is still bookable; the frontend ignores that reason--
export const isSlotTimeBookable = (slotBookability: {
  isBookable: boolean;
  isExistingApptWithinRequestedTime?: boolean;
}) =>
  slotBookability.isBookable ||
  slotBookability.isExistingApptWithinRequestedTime === true;

export const getDisabledServiceIds = (patient: BookingPatient) =>
  (patient.patientServices ?? [])
    .filter((item) => !item.isBookable)
    .map((item) => item.serviceId);

export const isSelectedServiceUnavailable = (patient: BookingPatient) =>
  !!patient.serviceId &&
  getDisabledServiceIds(patient).includes(patient.serviceId);

export const getSortedServiceOptions = <T extends { id: string }>(
  services: T[],
  patient: BookingPatient
) => {
  if (!patient.patientServices) return [...services];

  return services
    .map((service) => ({
      ...service,
      isBookable: !!patient.patientServices?.find(
        (item) => item.serviceId === service.id
      )?.isBookable,
    }))
    .sort((first, second) => {
      if (second.isBookable) return 1;

      if (first.isBookable) return -1;

      return 0;
    });
};

const findSelectedPatientService = (
  patient: SelectedPatient
): IFamilyBookingPatientService | null => {
  //--Exam & Cleaning books a resolved child service, the search reports the chosen one--
  const serviceIds = [patient.serviceId, patient.bookingServiceId];

  const patientService = (patient.patientServices ?? []).find((item) =>
    serviceIds.includes(item.serviceId)
  );

  return patientService ?? null;
};

export const resolvePatientBookability = (
  patient: SelectedPatient
): BookableSelectedPatient => {
  //--A patient with no clinic record has nothing to conflict with--
  if (!patient.id) return { ...patient, isBookable: true };

  const patientService = findSelectedPatientService(patient);

  const isBookable =
    patient.isBookable !== false && patientService?.isBookable !== false;

  return {
    ...patient,
    isBookable,
    appointment: patientService?.appointment ?? patient.appointment,
  };
};
